import { useEffect } from 'react';
import { parseSearchParams } from '@/utils/urlSearch';

/**
 * Reactive, listener-free sync of a single string URL query param into local
 * state — the generic building block for any single-value filter that has
 * no async "options to resolve against" step (a search box, a pill/select
 * with a fixed, always-available set of values). For member/date/timezone
 * fields that DO have that extra hydration concern, use `useUrlFilterSync.js`
 * instead.
 *
 * Re-derives on every `location.search` change (browser back/forward, an
 * inbound link, or a page's own writer echoing its value back) and only
 * calls `setValue` when the result actually differs — same discipline as
 * `useUrlDateRangeSync`, which is what keeps this safe to compose with a
 * page's own local `setState` calls without fighting them (this effect's
 * only dependency is `location.search`, so a same-page keystroke re-render
 * that doesn't touch the URL never re-triggers it).
 *
 * @param {{
 *   location: import('history').Location,
 *   paramKey: string,
 *   setValue: Function,
 *   normalize?: (raw: string) => string,
 * }} args
 */
export function useUrlParamSync({ location, paramKey, setValue, normalize }) {
  useEffect(() => {
    const params = parseSearchParams(location.search);
    const raw = typeof params[paramKey] === 'string' ? params[paramKey] : '';
    const next = normalize ? normalize(raw) : raw;
    setValue((prev) => (prev === next ? prev : next));
  }, [location.search, paramKey, setValue, normalize]);
}
